import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import WalkTestSecondStep from "./WalkTestSecondStep";
import ExitConfirmDialog from "./ExitConfirmDialog";
import { DialogTitle } from "@material-ui/core";
import axios from "../../utils/lib/axios";
import styles from "./walkTestSecondStep.module.css";
import { useDispatch } from "react-redux";
import { clearWalkTestFormData } from "../../store/actions/walkTestAction";
import {
  modalBackdropProps,
  MODAL_PAPER_SX,
} from "../../utils/styles/modal.overlay";
import DateTimeField from "./DateTimeField";
import ModalCloseButton from "../../components/UI/ModalCloseButton";

const validationSchema = Yup.object().shape({
  device: Yup.string().required("Required"),
  test_date: Yup.string().required("Date is required"),
  restStartTime: Yup.string().required("Required"),
  restEndTime: Yup.string()
    .required("Required")
    .test(
      "is-after-rest-start",
      "Must be after Rest Start time",
      function (value) {
        return (
          !value ||
          !this.parent.restStartTime ||
          value > this.parent.restStartTime
        );
      }
    ),
  walkStartTime: Yup.string()
    .required("Required")
    .test("is-after-rest-end", "Must be after Rest End time", function (value) {
      return (
        !value || !this.parent.restEndTime || value >= this.parent.restEndTime
      );
    }),
  walkEndTime: Yup.string()
    .required("Required")
    .test(
      "is-after-walk-start",
      "Must be after Walk Start time",
      function (value) {
        return (
          !value ||
          !this.parent.walkStartTime ||
          value > this.parent.walkStartTime
        );
      }
    ),
  recoveryStartTime: Yup.string()
    .required("Required")
    .test("is-after-walk-end", "Must be after Walk End time", function (value) {
      return (
        !value || !this.parent.walkEndTime || value >= this.parent.walkEndTime
      );
    }),
  recoveryEndTime: Yup.string()
    .required("Required")
    .test(
      "is-after-recovery-start",
      "Must be after Recovery Start time",
      function (value) {
        return (
          !value ||
          !this.parent.recoveryStartTime ||
          value > this.parent.recoveryStartTime
        );
      }
    ),
});

const defaultInitialValues = {
  device: "",
  test_date: new Date().toISOString().split("T")[0],
  restStartTime: "",
  restEndTime: "",
  walkStartTime: "",
  walkEndTime: "",
  recoveryStartTime: "",
  recoveryEndTime: "",
};

function WalkTestModal({
  open,
  onClose,
  admins,
  loadCompletedData,
  loadWaitingData,
  setAlert,
  icdTens,
  devices,
  onWalkTestReportGenerated,
}) {
  const dispatch = useDispatch();
  const formikRef = React.useRef();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(null);
  const [initialValues, setInitialValues] = useState(defaultInitialValues);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [onBackInitialValuesForm2, setOnBackInitialValuesForm2] =
    useState(null);

  const handleClose = () => {
    setShowExitConfirm(true);
  };

  const handleConfirmExit = () => {
    if (formikRef.current) {
      formikRef.current.resetForm();
      formikRef.current.setSubmitting(false);
      formikRef.current.setTouched({});
    }
    setShowExitConfirm(false);
    setStep(1);
    setFormData(null);
    setOnBackInitialValuesForm2(null);
    setInitialValues(defaultInitialValues);
    dispatch(clearWalkTestFormData());
    onClose();
  };

  const handleNext = async (values, { setSubmitting }) => {
    try {
      if (onBackInitialValuesForm2) {
        const { data } = await axios.put(`/6mwt/create-report`, {
          report_id: formData?.report_id,
          device_id: values?.device,
          test_date: values?.test_date,
          rest_start_time: values?.restStartTime,
          rest_end_time: values?.restEndTime,
          walk_start_time: values?.walkStartTime,
          walk_end_time: values?.walkEndTime,
          recovery_start_time: values?.recoveryStartTime,
          recovery_end_time: values?.recoveryEndTime,
        });
        setFormData({
          ...data?.data,
          report_id: data?.report_id,
          technician: data?.technician_name,
          date: data?.test_date,
        });
      } else {
        const { data } = await axios.post(`/6mwt/create-report`, {
          device_id: values?.device,
          test_date: values?.test_date,
          rest_start_time: values?.restStartTime,
          rest_end_time: values?.restEndTime,
          walk_start_time: values?.walkStartTime,
          walk_end_time: values?.walkEndTime,
          recovery_start_time: values?.recoveryStartTime,
          recovery_end_time: values?.recoveryEndTime,
        });
        setFormData({
          ...data?.data,
          report_id: data?.report_id,
          technician: data?.technician_name,
          date: data?.test_date,
        });
      }
      setInitialValues(values);
      setStep(2);
    } catch (error) {
      console.error("Submit failed:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = (values) => {
    console.log("form 2 from form one values", values);
    setOnBackInitialValuesForm2(values);
    setStep(1);
  };

  const labelStyle = {
    fontFamily: "Poppins",
    fontSize: "14px",
    fontWeight: 500,
    color: "var(--primary-text-color)",
    marginBottom: "8px",
  };

  const timeFieldContainer = {
    flex: 1,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minWidth: 0,
  };

  const timeLabelStyle = {
    fontFamily: "Poppins",
    fontSize: "14px",
    color: "var(--primary-text-color)",
    width: "80px",
    flexShrink: 0,
  };

  const errorMessageStyle = {
    color: "#d32f2f",
    fontSize: "12px",
    marginTop: "4px",
    fontFamily: "Poppins",
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        BackdropProps={modalBackdropProps}
        PaperProps={{
          sx: {
            ...MODAL_PAPER_SX,
            colorScheme: "dark",
            borderRadius: "4px",
            width: "1200px",
            maxWidth: "calc(100vw - 48px)",
            display: "flex",
            flexDirection: "column",
            height: step === 1 ? "70vh" : "90vh",
          },
        }}
      >
        <DialogTitle
          style={{
            padding: "24px 24px 0",
            position: "sticky",
            top: 0,
            backgroundColor: "var(--primary-popup-background-color)",
            zIndex: 1,
          }}
        >
          <ModalCloseButton onClick={handleClose} />

          <Typography
            style={{
              fontFamily: "Poppins",
              fontSize: "18px",
              fontWeight: 500,
              color: "var(--primary-text-color)",
              marginBottom: "24px",
              textAlign: "center",
            }}
          >
            6-Minute Walk Test
          </Typography>
        </DialogTitle>

        <DialogContent
          className={styles.walkTestModalContent}
          style={{
            padding: "24px",
            overflowY: "auto",
            flex: 1,
          }}
        >
          {step === 1 ? (
            <Formik
              innerRef={formikRef}
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleNext}
            >
              {({
                errors,
                touched,
                isSubmitting,
                values,
                isValid,
                dirty,
                setFieldValue,
                setFieldTouched,
                setValues,
              }) => (
                <Form
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "24px",
                  }}
                >
                  <div style={{ display: "flex", gap: "16px", width: "100%" }}>
                    <div style={{ flex: 1 }}>
                      <Typography
                        style={labelStyle}
                        className={`${styles.label} ${styles.required}`}
                      >
                        Device
                      </Typography>
                      <Field
                        name="device"
                        as="select"
                        className={styles.inputField}
                      >
                        <option value="">Select Device</option>
                        {devices.map((device) => (
                          <option key={device?.value} value={device?.value}>
                            {device?.label}
                          </option>
                        ))}
                      </Field>
                      {errors.device && touched.device && (
                        <div
                          style={{
                            color: "#d32f2f",
                            fontSize: "12px",
                            marginTop: "4px",
                          }}
                        >
                          {errors.device}
                        </div>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <Typography
                        style={labelStyle}
                        className={`${styles.label} ${styles.required}`}
                      >
                        Date
                      </Typography>
                      <Field name="test_date">
                        {({ field }) => (
                          <DateTimeField type="date" {...field} />
                        )}
                      </Field>
                      {errors.test_date && touched.test_date && (
                        <div
                          style={{
                            color: "#d32f2f",
                            fontSize: "12px",
                            marginTop: "4px",
                          }}
                        >
                          {errors.test_date}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <Typography
                      style={labelStyle}
                      className={`${styles.label} ${styles.required}`}
                    >
                      Rest
                    </Typography>
                    <div style={{ display: "flex", gap: "16px" }}>
                      <div style={timeFieldContainer}>
                        <Typography style={timeLabelStyle}>
                          Start Time
                        </Typography>
                        <Field name="restStartTime">
                          {({ field }) => (
                            <DateTimeField type="time" step="1" {...field} />
                          )}
                        </Field>
                        {errors.restStartTime && touched.restStartTime && (
                          <div
                            style={{
                              color: "#d32f2f",
                              fontSize: "12px",
                              marginTop: "4px",
                            }}
                          >
                            {errors.restStartTime}
                          </div>
                        )}
                      </div>
                      <div style={timeFieldContainer}>
                        <Typography style={timeLabelStyle}>End Time</Typography>
                        <Field name="restEndTime">
                          {({ field }) => (
                            <DateTimeField
                              type="time"
                              step="1"
                              {...field}
                              className={
                                errors.restEndTime && touched.restEndTime
                                  ? styles.inputError
                                  : ""
                              }
                              onChange={(e) => {
                                setFieldTouched("restEndTime", true, false);
                                const value = e.target.value;
                                if (
                                  value &&
                                  (!values.restStartTime ||
                                    value > values.restStartTime)
                                ) {
                                  const [hours, minutes, seconds = "00"] =
                                    value.split(":").map(Number);
                                  const date = new Date();
                                  date.setHours(hours);
                                  date.setMinutes(minutes);
                                  date.setSeconds(seconds);

                                  date.setMinutes(date.getMinutes() + 6);

                                  const walkEnd = date
                                    .toTimeString()
                                    .split(" ")[0];

                                  setValues({
                                    ...values,
                                    restEndTime: value,
                                    walkStartTime: value,
                                    walkEndTime: walkEnd,
                                    recoveryStartTime: walkEnd,
                                  });
                                } else {
                                  setFieldValue("restEndTime", value);
                                }
                              }}
                            />
                          )}
                        </Field>
                        {errors.restEndTime && touched.restEndTime && (
                          <div
                            style={{
                              color: "#d32f2f",
                              fontSize: "12px",
                              marginTop: "4px",
                            }}
                          >
                            {errors.restEndTime}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <Typography
                      style={labelStyle}
                      className={`${styles.label} ${styles.required}`}
                    >
                      Walk
                    </Typography>
                    <div style={{ display: "flex", gap: "16px" }}>
                      <div style={timeFieldContainer}>
                        <Typography style={timeLabelStyle}>
                          Start Time
                        </Typography>
                        <Field name="walkStartTime">
                          {({ field }) => (
                            <DateTimeField type="time" step="1" {...field} />
                          )}
                        </Field>
                        {errors.walkStartTime && touched.walkStartTime && (
                          <div style={errorMessageStyle}>
                            {errors.walkStartTime}
                          </div>
                        )}
                      </div>
                      <div style={timeFieldContainer}>
                        <Typography style={timeLabelStyle}>End Time</Typography>
                        <Field name="walkEndTime">
                          {({ field }) => (
                            <DateTimeField
                              type="time"
                              step="1"
                              {...field}
                              className={
                                errors.walkEndTime && touched.walkEndTime
                                  ? styles.inputError
                                  : ""
                              }
                              onChange={(e) => {
                                field.onChange(e);
                                setFieldTouched("walkEndTime", true, false);
                                const value = e.target.value;
                                if (
                                  value &&
                                  (!values.walkStartTime ||
                                    value > values.walkStartTime)
                                ) {
                                  setFieldValue("walkEndTime", value);
                                  setFieldValue("recoveryStartTime", value);
                                }
                              }}
                            />
                          )}
                        </Field>
                        {errors.walkEndTime && touched.walkEndTime && (
                          <div style={errorMessageStyle}>
                            {errors.walkEndTime}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <Typography
                      style={labelStyle}
                      className={`${styles.label} ${styles.required}`}
                    >
                      Recovery
                    </Typography>
                    <div style={{ display: "flex", gap: "16px" }}>
                      <div style={timeFieldContainer}>
                        <Typography style={timeLabelStyle}>
                          Start Time
                        </Typography>
                        <Field name="recoveryStartTime">
                          {({ field }) => (
                            <DateTimeField type="time" step="1" {...field} />
                          )}
                        </Field>
                        {errors.recoveryStartTime &&
                          touched.recoveryStartTime && (
                            <div style={errorMessageStyle}>
                              {errors.recoveryStartTime}
                            </div>
                          )}
                      </div>
                      <div style={timeFieldContainer}>
                        <Typography style={timeLabelStyle}>End Time</Typography>
                        <Field name="recoveryEndTime">
                          {({ field }) => (
                            <DateTimeField
                              type="time"
                              step="1"
                              {...field}
                              className={
                                errors.recoveryEndTime &&
                                touched.recoveryEndTime
                                  ? styles.inputError
                                  : ""
                              }
                              onChange={(e) => {
                                field.onChange(e);
                                setFieldTouched(
                                  "recoveryEndTime",
                                  true,
                                  false,
                                );
                                const value = e.target.value;
                                if (
                                  value &&
                                  (!values.recoveryStartTime ||
                                    value > values.recoveryStartTime)
                                ) {
                                  setFieldValue("recoveryEndTime", value);
                                }
                              }}
                            />
                          )}
                        </Field>
                        {errors.recoveryEndTime && touched.recoveryEndTime && (
                          <div style={errorMessageStyle}>
                            {errors.recoveryEndTime}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: "8px",
                    }}
                  >
                    <Button
                      type="submit"
                      disabled={isSubmitting || !isValid}
                      style={{
                        padding: "8px 24px",
                        backgroundColor: "var(--primary-theme-color)",
                        color: "var(--primary-button-text-color)",
                        borderRadius: "4px",
                        textTransform: "none",
                        fontFamily: "Poppins",
                        fontWeight: 500,
                        fontSize: "14px",
                        minWidth: "120px",
                        opacity: isSubmitting || !isValid ? "0.7" : "1",
                      }}
                    >
                      {isSubmitting ? "Next..." : "Next"}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          ) : (
            <WalkTestSecondStep
              onBack={handleBack}
              formData={formData}
              onClose={handleClose}
              admins={admins}
              handleConfirmExit={handleConfirmExit}
              loadCompletedData={loadCompletedData}
              loadWaitingData={loadWaitingData}
              setAlert={setAlert}
              icdTens={icdTens}
              onBackInitialValuesForm2={onBackInitialValuesForm2}
              onWalkTestReportGenerated={onWalkTestReportGenerated}
            />
          )}
        </DialogContent>
      </Dialog>

      <ExitConfirmDialog
        open={showExitConfirm}
        onClose={() => setShowExitConfirm(false)}
        onConfirm={handleConfirmExit}
        title="Are you sure you want to exit this report?"
      />
    </>
  );
}

export default WalkTestModal;
